import React, { PropTypes, Component } from 'react';
import { connect } from 'react-redux';
import ReactDOM from 'react-dom';

import CommentsList from 'components/CommentsList';
import * as actions from 'actions';
import ResourceComment from './ResourceComment';
import { getComments, getCommentsPaging, getNestedCommentsPagingState, getInfoState, getCommentsSort } from 'selectors';
import { CommentShape } from 'entities';
import ResourceCommentsPageSelector from '../ResourceCommentsPageSelector';
import Comment from 'components/CommentsList/Comment';
import InfoBar from 'components/InfoBar';
import './style.scss';

function highlihgtedIdForUrlParams(urlParams = {}) {
  let commentOrReplyId = null;
  const { commentId, commentsPage, repliesPage, replyId } = urlParams;

  if(commentsPage && commentId) {
    if(repliesPage && replyId) {
      commentOrReplyId = Number(replyId);
    }
    else {
      commentOrReplyId = Number(commentId);
    }
  }

  return commentOrReplyId;
}

class ResourceCommentsList extends Component {

  componentDidMount() {
    this.props.onDidMount && this.props.onDidMount(this.props.sort);
  }

  componentDidUpdate(prevProps) {
    this.props.onDidUpdate && this.props.onDidUpdate(prevProps, this.props.sort);

    if(this.commentToScrollTo) {
      const commentDOMNode = ReactDOM.findDOMNode(this.commentToScrollTo);
      const scrollElementDOMNode = ReactDOM.findDOMNode(this.props.scrollElementGetter());

      window.scrollTo(0, commentDOMNode.getBoundingClientRect().top + window.scrollY - scrollElementDOMNode.offsetTop);
      this.commentToScrollTo = null;
    }
  }

  componentWillUnmount() {
    this.props.onWillUnmount && this.props.onWillUnmount();
  }

  renderComments(comments, repliesResourceType) {
    return comments.map(comment => {
      const paging = this.props.nestedCommentsPaging[comment.repliesQueryString];
      const commentOrReplyId = highlihgtedIdForUrlParams(this.props.urlParams);
      const commentOrReplyRef = commentOrReplyId !== null ? value => this.commentToScrollTo = value : null;

      return (
        <ResourceComment
          key={comment.id}
          commentOrReplyRef={commentOrReplyRef}
          commentOrReplyId={commentOrReplyId}
          canBeRepliedTo={this.props.canCommentsBeRepliedTo}
          repliesResourceType={repliesResourceType}
          repliesPagesCount={paging ? paging.pagesCount : 0}
          comment={comment}
          isFetchingReplies={paging && paging.isFetching}
        />
      );
    });
  }

  renderLoadingComments() {
    return [
      <Comment key="loader1" isEmpty />,
      <Comment key="loader2" isEmpty />,
      <Comment key="loader3" isEmpty />
    ];
  }

  render() {
    return (
      <div className="ResourceCommentsList">
        {this.props.isSuccessInfoBarVisible &&
          <InfoBar
            type="primary"
            message={this.props.successInfoBarMessage}
            title={this.props.successInfoBarTitle}
            onClose={this.props.onInfoBarClose} />
        }

        <CommentsList sortOptionsPairs={[
            {value: 'recent', title: __('members.comments.sortOptions.sortRecent')},
            {value: 'useful', title: __('members.comments.sortOptions.sortUseful')}
          ]}
          selectedSortOption={this.props.sort}
          onSortOptionChange={this.props.onSortOptionChange}
          isFollowed={this.props.isFollowed}
          allowsSorting={this.props.comments.length > 0}
          onFollow={this.props.onFollow}
          onUnfollow={this.props.onUnfollow}>
          {(() => {
            if(this.props.comments.length > 0) {
              return this.renderComments(this.props.comments, this.props.repliesResourceType);
            }
            else if(this.props.isFetching) {
              return this.renderLoadingComments();
            }
          })()}
        </CommentsList>

        {this.props.pagesCount > 1 &&
          <div className="pagination-wrapper">
            <div className="wrapper-xlarge">
              <ResourceCommentsPageSelector
                pageSize={process.env.KOOTH_PAGE_SIZE}
                sort={this.props.sort}
                resourceType={this.props.resourceType}
                resourceId={this.props.resourceId} />
            </div>
          </div>
        }
      </div>
    );
  }
}

ResourceCommentsList.propTypes = {
  resourceId: PropTypes.number,
  resourceType: PropTypes.string,
  repliesResourceType: PropTypes.string,
  comments: PropTypes.arrayOf(CommentShape),
  nestedCommentsPaging: PropTypes.object,
  isFetching: PropTypes.bool,
  sort: PropTypes.string,
  pagesCount: PropTypes.number,
  currentPage: PropTypes.number,
  isSuccessInfoBarVisible: PropTypes.bool,
  canCommentsBeRepliedTo: PropTypes.bool,
  isFollowed: PropTypes.bool,
  successInfoBarMessage: PropTypes.string,
  successInfoBarTitle: PropTypes.string,
  urlParams: PropTypes.object,
  onDidMount: PropTypes.func,
  onWillUnmount: PropTypes.func,
  onCommentViewReplies: PropTypes.func,
  onCommentHideReplies: PropTypes.func,
  onCommentReply: PropTypes.func,
  onCommentCancelReply: PropTypes.func,
  onInfoBarClose: PropTypes.func,
  onSortOptionChange: PropTypes.func,
  onDidUpdate: PropTypes.func,
  onFollow: PropTypes.func,
  onUnfollow: PropTypes.func,
  scrollElementGetter: PropTypes.func
};

const mapStateToProps = (state, props) => {
  const sort = getCommentsSort(state);
  const query = {resourceId: props.resourceId, resourceType: props.resourceType, sort};
  const queryString = JSON.stringify(query);
  const { page, isFetching, pagesCount } = getCommentsPaging(state, null, { queryString });
  const successState = getInfoState(state, 'success');

	return {
    comments: getComments(state, { queryString, page }),
    nestedCommentsPaging: getNestedCommentsPagingState(state),
    sort,
    isFetching,
    currentPage: page,
    pagesCount,
    isSuccessInfoBarVisible: successState.visible,
    successInfoBarMessage: successState.message,
    successInfoBarTitle: successState.title
  };
};

const mapDispatchToProps = (dispatch, props) => {
  const reset = () => {
    dispatch(actions.commentReplyCancel());
    dispatch(actions.commentReset('firstLevel'));
    dispatch(actions.commentEmojisHide('firstLevel'));
    dispatch(actions.commentsResetRepliesRelatedProperties());
  };

  return {
    onDidMount: sort => {
      const { urlParams = {} } = props;
      const { commentsPage, commentId, repliesPage, replyId } = urlParams;

      const query = {resourceId: props.resourceId, resourceType: props.resourceType, sort};
      dispatch(actions.commentsFetch(Number(commentsPage) || 1, process.env.KOOTH_PAGE_SIZE, query));

      if(repliesPage && replyId) {
        const query = {resourceId: Number(commentId), resourceType: props.repliesResourceType};
        dispatch(actions.commentsFetch(Number(repliesPage) || 1, process.env.KOOTH_COMMENT_REPLIES_PAGE_SIZE, query));
      }

      const highlightedCommentId = highlihgtedIdForUrlParams(urlParams);

      if(highlightedCommentId !== null) {
        dispatch(actions.commentUpdate(highlightedCommentId, null, {isHighlighted: true}));

        if(highlightedCommentId !== commentId) {
          dispatch(actions.commentUpdate(commentId, null, {hasHighlightedReply: true}));
        }
      }
    },
    onDidUpdate: (prevProps, sort) => {
      if(prevProps.resourceId !== props.resourceId) {
        reset();

        const query = {resourceId: props.resourceId, resourceType: props.resourceType, sort};
        dispatch(actions.commentsFetch(1, process.env.KOOTH_PAGE_SIZE, query));
      }
    },
    onSortOptionChange: sort => {
      const query = {resourceId: props.resourceId, resourceType: props.resourceType, sort};

      dispatch(actions.commentsFetch(1, process.env.KOOTH_PAGE_SIZE, query));
      dispatch(actions.commentsChangeSort(sort));
    },
    onWillUnmount: () => {
      reset();
    },
    onInfoBarClose: () => {
      dispatch(actions.infoClose('success'));
    }
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ResourceCommentsList);



/** WEBPACK FOOTER **
 ** ./src/members/Main/__shared__/containers/ResourceCommentsList/index.js
 **/